import React, { useEffect, useMemo, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../TextField/TextFeild";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import { HUMAN_TIMEZONES } from "../../constants";
import { scheduleChatMessage, updateScheduledChatMessage } from "../../store/slices/chatSlice";
import { decodedText, toUnicode } from "../../helpers/utils";
import { getScheduledMessagePhase } from "../../Pages/Dashboard/Moderator/Chat/scheduledMessageHelpers";
import {
    buildPinRemovalApiFieldsFromForm,
    buildScheduleApiFieldsFromForm,
    buildScheduleFormFromAttrs,
    formatScheduledLocalDisplay,
    ianaFromTzKey,
    parseInTz,
    tzKeyFromIana,
} from "../../Pages/Dashboard/Moderator/Chat/scheduledTimeUtils";

const buildPinRemovalEditFormValues = (editItem, stationIana) => {
    const attrs = editItem?.attributes || {};
    const schedule = buildScheduleFormFromAttrs(attrs, stationIana);

    return {
        body: decodedText(attrs.text || attrs.sanitizedText || "") || "",
        tz: schedule.tz,
        date: "",
        time: "",
        schedulePinRemoval: true,
        removalDate: schedule.removalDate,
        removalTime: schedule.removalTime,
        removalTz: schedule.removalTz,
    };
};

const buildEditFormValues = (editItem, stationIana) => {
    const attrs = editItem?.attributes || {};
    const schedule = buildScheduleFormFromAttrs(attrs, stationIana);

    return {
        body: decodedText(attrs.text || attrs.sanitizedText || "") || "",
        ...schedule,
    };
};

const time24Regex = /^([01]?\d|2[0-3]):[0-5]\d$/;

const buildScheduleSchema = () =>
    Yup.object({
        body: Yup.string().trim().required("Message is required"),
        tz: Yup.string().required("Timezone is required"),
        date: Yup.string().required("Date is required"),
        time: Yup.string()
            .required("Time is required")
            .matches(time24Regex, "Use a valid time")
            .test("send-future", "Message must be scheduled at least 5 minutes in advance", function (value) {
                const { date, tz } = this.parent;
                if (!date || !value || !tz) return true;
                try {
                    const scheduledTime = parseInTz(date, value, tz);
                    const now = moment.tz(HUMAN_TIMEZONES[tz] || tz);
                    return scheduledTime.diff(now, "minutes", true) >= 5;
                } catch (e) {
                    return false;
                }
            }),
        pinMessage: Yup.boolean(),
        schedulePinRemoval: Yup.boolean(),
        removalDate: Yup.string().when(["pinMessage", "schedulePinRemoval"], {
            is: (pinMessage, schedulePinRemoval) => Boolean(pinMessage && schedulePinRemoval),
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        removalTz: Yup.string().when(["pinMessage", "schedulePinRemoval"], {
            is: (pinMessage, schedulePinRemoval) => Boolean(pinMessage && schedulePinRemoval),
            then: (schema) => schema.required("Timezone is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        removalTime: Yup.string().when(["pinMessage", "schedulePinRemoval"], {
            is: (pinMessage, schedulePinRemoval) => Boolean(pinMessage && schedulePinRemoval),
            then: (schema) =>
                schema
                    .required("Time is required")
                    .matches(time24Regex, "Use a valid time")
                    .test(
                        "removal-after-send",
                        "Pin removal must be at least 1 minute after the message is sent",
                        function (removalTimeVal) {
                            const p = this.parent;
                            if (!p.pinMessage || !p.schedulePinRemoval || !removalTimeVal || !p.removalDate) {
                                return true;
                            }
                            try {
                                const removalTzKey = p.removalTz || p.tz;
                                const sendAt = parseInTz(p.date, p.time, p.tz);
                                const removalAt = parseInTz(p.removalDate, removalTimeVal, removalTzKey);
                                return removalAt.diff(sendAt, "minutes", true) >= 1;
                            } catch (e) {
                                return false;
                            }
                        }
                    ),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

const buildPinRemovalSchema = (sentAtIso) =>
    Yup.object({
        schedulePinRemoval: Yup.boolean(),
        removalDate: Yup.string().when("schedulePinRemoval", {
            is: true,
            then: (schema) => schema.required("Date is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        removalTz: Yup.string().when("schedulePinRemoval", {
            is: true,
            then: (schema) => schema.required("Timezone is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
        removalTime: Yup.string().when("schedulePinRemoval", {
            is: true,
            then: (schema) =>
                schema
                    .required("Time is required")
                    .matches(time24Regex, "Use a valid time")
                    .test(
                        "removal-after-send",
                        "Pin removal must be at least 1 minute after the message was sent",
                        function (removalTimeVal) {
                            const p = this.parent;
                            if (!p.schedulePinRemoval || !removalTimeVal || !p.removalDate || !sentAtIso) {
                                return true;
                            }
                            try {
                                const removalTzKey = p.removalTz || p.tz;
                                const sendAt = moment(sentAtIso);
                                const removalAt = parseInTz(p.removalDate, removalTimeVal, removalTzKey);
                                return removalAt.diff(sendAt, "minutes", true) >= 1;
                            } catch (e) {
                                return false;
                            }
                        }
                    )
                    .test("removal-future", "Pin removal must be scheduled in the future", function (removalTimeVal) {
                        const p = this.parent;
                        if (!p.schedulePinRemoval || !removalTimeVal || !p.removalDate) {
                            return true;
                        }
                        try {
                            const removalTzKey = p.removalTz || p.tz;
                            const removalAt = parseInTz(p.removalDate, removalTimeVal, removalTzKey);
                            const tz = HUMAN_TIMEZONES[removalTzKey] || removalTzKey;
                            return removalAt.isAfter(moment.tz(tz));
                        } catch (e) {
                            return false;
                        }
                    }),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

const ScheduleChatMessageModal = ({
    show,
    onClose,
    initialBody = "",
    initialPin = false,
    editItem = null,
    onScheduleSuccess,
}) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const scheduleMessageLoading = useSelector((state) => state.chat.scheduleMessageLoading);
    const prevShowRef = useRef(false);
    const isEditMode = Boolean(editItem?.id);
    const isPinRemovalOnlyEdit =
        isEditMode && getScheduledMessagePhase(editItem) === "pin_removal";
    const editAttrs = editItem?.attributes;
    const sentAtIso = useMemo(() => {
        if (editAttrs?.scheduledLocalDate && editAttrs?.scheduledLocalTime && editAttrs?.scheduledTimezone) {
            const tzKey = tzKeyFromIana(editAttrs.scheduledTimezone);
            return parseInTz(editAttrs.scheduledLocalDate, editAttrs.scheduledLocalTime, tzKey).toISOString();
        }
        return editAttrs?.scheduledAt;
    }, [editItem?.id, editAttrs?.scheduledAt, editAttrs?.scheduledLocalDate, editAttrs?.scheduledLocalTime, editAttrs?.scheduledTimezone]);
    const stationIana = user?.attributes?.stationTimezone;

    const validationSchema = useMemo(
        () =>
            isPinRemovalOnlyEdit ? buildPinRemovalSchema(sentAtIso) : buildScheduleSchema(),
        [isPinRemovalOnlyEdit, sentAtIso]
    );

    const formik = useFormik({
        initialValues: {
            body: "",
            tz: "",
            date: "",
            time: "",
            pinMessage: false,
            schedulePinRemoval: false,
            removalDate: "",
            removalTime: "",
            removalTz: "",
        },
        enableReinitialize: false,
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                if (isEditMode) {
                    if (isPinRemovalOnlyEdit) {
                        await dispatch(
                            updateScheduledChatMessage({
                                id: editItem.id,
                                message: "Pin removal schedule updated.",
                                schedulePinRemoval: Boolean(values.schedulePinRemoval),
                                ...buildPinRemovalApiFieldsFromForm(values),
                            })
                        ).unwrap();
                    } else {
                        await dispatch(
                            updateScheduledChatMessage({
                                id: editItem.id,
                                text: toUnicode(values.body),
                                schedulePinRemoval: Boolean(values.schedulePinRemoval),
                                ...buildScheduleApiFieldsFromForm(values),
                            })
                        ).unwrap();
                    }
                } else {
                    await dispatch(
                        scheduleChatMessage({
                            text: toUnicode(values.body),
                            pinMessage: Boolean(values.pinMessage),
                            ...buildScheduleApiFieldsFromForm(values),
                        })
                    ).unwrap();
                }
                resetForm();
                onClose();
                onScheduleSuccess?.();
            } catch {
                /* toast handled in thunk */
            } finally {
                setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        const wasOpen = prevShowRef.current;
        prevShowRef.current = show;

        if (!show) return;
        if (wasOpen) return;

        if (isEditMode) {
            formik.resetForm({
                values: isPinRemovalOnlyEdit
                    ? buildPinRemovalEditFormValues(editItem, stationIana)
                    : buildEditFormValues(editItem, stationIana),
            });
        } else {
            const tzKey = tzKeyFromIana(stationIana);
            const ianaForMoment = ianaFromTzKey(tzKey) || stationIana || moment.tz.guess();
            const m = moment().tz(ianaForMoment).add(6, "minutes");
            const removalDefault = m.clone().add(1, "hour");
            formik.resetForm({
                values: {
                    body: initialBody || "",
                    tz: tzKey,
                    date: m.format("YYYY-MM-DD"),
                    time: m.format("HH:mm"),
                    schedulePinRemoval: false,
                    removalDate: removalDefault.format("YYYY-MM-DD"),
                    removalTime: removalDefault.format("HH:mm"),
                    removalTz: tzKey,
                },
            });
        }
        formik.setTouched({});
    }, [show, initialBody, editItem?.id, isEditMode, isPinRemovalOnlyEdit, stationIana]);

    const handlePinRemovalToggle = (e) => {
        const checked = e.target.checked;
        formik.setFieldValue("schedulePinRemoval", checked);
        if (checked && formik.values.date && formik.values.time && formik.values.tz) {
            try {
                const sendAt = parseInTz(formik.values.date, formik.values.time, formik.values.tz).add(1, "hour");
                formik.setFieldValue("removalDate", sendAt.format("YYYY-MM-DD"));
                formik.setFieldValue("removalTime", sendAt.format("HH:mm"));
                if (!formik.values.removalTz) {
                    formik.setFieldValue("removalTz", formik.values.tz);
                }
            } catch {
                /* ignore */
            }
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="md" backdrop="static" scrollable>
            <form onSubmit={formik.handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">
                        {isPinRemovalOnlyEdit
                            ? "Edit scheduled pin removal"
                            : isEditMode
                              ? "Edit scheduled message"
                              : "Schedule send"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2" style={{ maxHeight: "min(70vh, 640px)" }}>
                    <div className="px-1">
                        {isPinRemovalOnlyEdit && (
                            <>
                                <p className="small text-muted mb-2">
                                    This message was sent and pinned. Change when the pin is removed, or turn off
                                    scheduled removal to keep it pinned.
                                </p>
                                <CustomInput
                                    label="Message"
                                    id="schedule-message-body-readonly"
                                    name="body"
                                    value={formik.values.body}
                                    disabled
                                    isTextArea
                                />
                                {editAttrs && (
                                    <p className="small text-muted mb-3">
                                        Sent {formatScheduledLocalDisplay(editAttrs, "send")}
                                    </p>
                                )}
                            </>
                        )}

                        {!isPinRemovalOnlyEdit && (
                            <>
                        <CustomInput
                            label="Message"
                            id="schedule-message-body"
                            name="body"
                            value={formik.values.body}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.body && formik.errors.body}
                            required
                            isTextArea
                        />

                        <TimezoneSelect
                            label="Timezone"
                            id="schedule-message-timezone"
                            name="tz"
                            value={formik.values.tz}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tz && formik.errors.tz}
                        />
                        <CustomInput
                            type="date"
                            label="Date"
                            name="date"
                            value={formik.values.date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.date && formik.errors.date}
                        />
                        <CustomInput
                            type="time"
                            label="Time"
                            name="time"
                            value={formik.values.time}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.time && formik.errors.time}
                        />

                        <div className="form-check mt-3 mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="schedule-pin-message"
                                checked={formik.values.pinMessage}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    formik.setFieldValue("pinMessage", checked);
                                    if (!checked) {
                                        formik.setFieldValue("schedulePinRemoval", false);
                                    }
                                }}
                            />
                            <label className="form-check-label" htmlFor="schedule-pin-message">
                                Pin this message when it is sent
                            </label>
                        </div>

                        <div className="form-check mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="schedule-pin-removal"
                                checked={formik.values.schedulePinRemoval}
                                disabled={!formik.values.pinMessage}
                                onChange={handlePinRemovalToggle}
                            />
                            <label className="form-check-label" htmlFor="schedule-pin-removal">
                                Schedule pin removal
                            </label>
                        </div>
                            </>
                        )}

                        {(isPinRemovalOnlyEdit ||
                            (formik.values.pinMessage && formik.values.schedulePinRemoval)) && (
                            <>
                                {!isPinRemovalOnlyEdit && (
                                    <p className="small text-muted mb-2">
                                        The message will be pinned when it is sent. When should the pin be
                                        removed?
                                    </p>
                                )}
                                {isPinRemovalOnlyEdit && (
                                    <div className="form-check mb-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="schedule-pin-removal-edit"
                                            checked={formik.values.schedulePinRemoval}
                                            onChange={(e) =>
                                                formik.setFieldValue("schedulePinRemoval", e.target.checked)
                                            }
                                        />
                                        <label className="form-check-label" htmlFor="schedule-pin-removal-edit">
                                            Schedule pin removal
                                        </label>
                                    </div>
                                )}
                                <TimezoneSelect
                                    label="Timezone"
                                    id="schedule-pin-removal-timezone"
                                    name="removalTz"
                                    value={formik.values.removalTz}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.removalTz && formik.errors.removalTz}
                                />
                                <CustomInput
                                    type="date"
                                    label="Removal date"
                                    name="removalDate"
                                    value={formik.values.removalDate}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.removalDate && formik.errors.removalDate}
                                />
                                <CustomInput
                                    type="time"
                                    label="Removal time"
                                    name="removalTime"
                                    value={formik.values.removalTime}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.removalTime && formik.errors.removalTime}
                                />
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-top">
                    <button type="button" className="btn btn-lg btn-outline-danger" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-lg btn-secondary"
                        disabled={formik.isSubmitting || scheduleMessageLoading}
                    >
                        {formik.isSubmitting || scheduleMessageLoading
                            ? isEditMode
                                ? "Saving..."
                                : "Scheduling..."
                            : isPinRemovalOnlyEdit
                              ? "Save pin removal"
                              : isEditMode
                                ? "Save changes"
                                : "Schedule send"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default ScheduleChatMessageModal;